import JSZip from "jszip";
import { saveAs } from "file-saver";
import { generatePHLExcel } from "./PHLExcelDownload";
import { generateCPDExcel } from "./CPDExcelDownload";
import supabase from "./supabaseClient"; 

const BUCKET_FOLDER_MAP = {
  "reflective-written-accounts": "Reflective Written Accounts",
  "reflective-discussion": "Reflective Discussion",
  "practice-feedback": "Practice Feedback",
  "cpd-evidence": "CPD Evidence",
  "reval-confirmation": "Revalidation Confirmation",
};

// List of all buckets to include
const BUCKETS = [
  "reflective-written-accounts",
  "reflective-discussion",
  "cpd-evidence",
  "practice-feedback",
  "reval-confirmation",
];

// Fetch files for a user from a single bucket
const fetchSupabaseFilesByBucket = async (bucket, userId, startDate, endDate) => {
  const { data, error } = await supabase
  .from('revalidation_documents')
  .select('bucket, file_path, upload_date')
  .eq('bucket', bucket)
  .eq('user_id', userId)
  .gte('upload_date', startDate)
  .lte('upload_date', endDate)

  if (error) {
    console.warn(`Failed to list from ${bucket}:`, error.message);
    return data;
  }

  return data.map(file => ({
  bucket: file.bucket,
  path: file.file_path,
  filename: file.file_path.split('/').pop() || 'document',
}));
};

// Fetch and flatten all files from every bucket
const fetchAllSupabaseFiles = async (userId, startDate, endDate) => {
  const allFiles = [];

  for (const bucket of BUCKETS) {
    const bucketFiles = await fetchSupabaseFilesByBucket(bucket, userId, startDate, endDate);
    allFiles.push(...bucketFiles);
  }

  return allFiles;
};

// Main ZIP download function
export const downloadRevalidationZip = async (profile, phl, cpd) => {
  try {
    const zip = new JSZip();

    // --- Excel: Practice Hours Log ---
    const phlExcel = await generatePHLExcel(phl, profile);
    if (phlExcel) {
      zip.file(phlExcel.fileName, phlExcel.buffer);
    }

    // --- Excel: CPD Log ---
    const cpdExcel = await generateCPDExcel(cpd, profile);
    if (cpdExcel) {
      zip.file(cpdExcel.fileName, cpdExcel.buffer);
    }

    // --- Supabase Files ---
    const supabaseFiles = await fetchAllSupabaseFiles(
      profile.id,
      profile.reval_members.last_renewal_date,
      profile.reval_members.renewal_date
    );
    if (!Array.isArray(supabaseFiles)) {
      console.error("supabaseFiles is not an array:", supabaseFiles);
      return;
    }

    for (const { bucket, path, filename } of supabaseFiles) {
      const { data, error } = await supabase.storage.from(bucket).download(path);
      if (error) {
        console.warn(`Failed to download ${path} from ${bucket}:`, error.message);
        continue;
      }

      const fileBuffer = await data.arrayBuffer();
      const folderName = BUCKET_FOLDER_MAP[bucket] || bucket;
      zip.folder(folderName)?.file(filename, fileBuffer);
    }

    // --- Final ZIP ---
    const zipBlob = await zip.generateAsync({ type: "blob" });
    const fileName = `${profile.first_name}_${profile.surname}_Revalidation Portfolio.zip`;
    saveAs(zipBlob, fileName);
  } catch (err) {
    console.error("Error creating ZIP:", err);
  }
    return;
};